import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getGoogleMapsApiVersion } from '@rsos/base-utils/metaTags';
import {
  BASEMAP_GOOGLE_HYBRID,
  BASEMAP_GOOGLE_ROADMAP,
} from '@rsos/constants/basemapAddonNames';
import exportToGlobalNamespace from '@rsos/utils/exportToGlobalNamespace';

/**
 * Handle loading google maps if window.google is undefined. Returns the Google
 * maps instance, a ref, and an initialized flag for use in other components.
 * @param {String} apiKey - Google maps API key
 * @param {String} mapId - Google maps ID for applying custom styling (e.g., light or dark mode).
 * @param {Object} options - Google maps options
 * @param {Object} styles - Google maps styles
 */
const useGoogleMap = ({
  apiKey = '',
  mapId = '',
  options = null,
  styles = null,
}) => {
  const [mapState, setMapState] = useState({
    mapInstance: null,
    initialized: false,
  });

  const mapRef = useRef();

  const basemap = useSelector(state => state.sinatra?.ui?.basemap);

  const isGoogleBasemap = useMemo(() => {
    return (
      basemap === BASEMAP_GOOGLE_HYBRID || basemap === BASEMAP_GOOGLE_ROADMAP
    );
  }, [basemap]);

  const googleMapsApiVersion = getGoogleMapsApiVersion() || 'quarterly';

  useEffect(() => {
    const handleScriptLoad = () => {
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        ...options,
        ...styles,
        ...(mapId && {
          mapId,
          renderingType: window.google.maps.RenderingType?.VECTOR, // Use vector rendering type so that hybrid basemap labels are visible.
        }),
        mapTypeControl: options?.mapTypeControl || false,
        mapTypeId: options?.mapTypeId
          ? options.mapTypeId
          : isGoogleBasemap
            ? basemap
            : window.google.maps.MapTypeId?.ROADMAP,
      });

      setMapState({
        ...mapState,
        mapInstance,
        initialized: true,
      });

      exportToGlobalNamespace('map', {
        mapProvider: 'google',
        mapInstance,
      });

      delete window.handleScriptLoad;
    };

    const loadMapScript = () => {
      window.handleScriptLoad = handleScriptLoad;

      if (window.google?.maps?.Map) {
        handleScriptLoad();
        return;
      }
      if (window.alreadyRequestedGoogleMaps) {
        const gmapsScript = document.getElementById('google-maps-api');
        if (gmapsScript) {
          gmapsScript.addEventListener('load', handleScriptLoad);
        }
        return;
      }
      window.alreadyRequestedGoogleMaps = true;
      const s = document.createElement('script');
      s.id = 'google-maps-api';
      s.type = 'text/javascript';
      s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=marker&v=${googleMapsApiVersion}&callback=handleScriptLoad`;
      const x = document.getElementsByTagName('script')[0];
      x.parentNode.insertBefore(s, x);
    };

    loadMapScript();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId]);

  const cleanUpMap = () => {
    if (mapState.mapInstance) {
      mapState.mapInstance.setMap(null);
      // TODO: confirm if this works
      window.google.maps.event.clearInstanceListeners(window);
      window.google.maps.event.clearInstanceListeners(document);
    }
  };

  return { mapRef, cleanUpMap, ...mapState };
};

export default useGoogleMap;
